<template>
  <DSFormField
    :id="id"
    :label="formFieldLabel"
    :tooltip="props.tooltip"
    :hidden-label="props.hiddenLabel"
    :required="props.required"
    :disabled="props.disabled"
    :error-messages="dsFormField.errorMessages.value"
    :name="props.name"
    :class="switchContainerClasses"
  >
    <div
      :class="DS_SWITCH_CLASSES.InputContainer"
      data-cy="ds-switch__input-container"
    >
      <input
        v-if="shouldRenderUncheckedInput"
        type="hidden"
        :name="props.name"
        :value="props.uncheckedValue"
        :disabled="props.disabled"
      />
      <input
        :id="id"
        ref="inputRef"
        type="checkbox"
        :name="props.name"
        :value="checkedSubmitValue"
        :checked="isChecked"
        :disabled="props.disabled"
        :class="inputClasses"
        data-cy="ds-switch__input"
        role="switch"
        :aria-checked="isChecked ? 'true' : 'false'"
        :aria-invalid="hasErrors ? 'true' : undefined"
        @change="onToggle"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
      />
      <div
        :class="plateClasses"
        data-cy="ds-switch__plate"
      >
        <div
          :class="thumbClasses"
          data-cy="ds-switch__thumb"
        >
          <DSIcon
            :icon="thumbIconName"
            :class="DS_SWITCH_CLASSES.ThumbIcon"
            size="sm"
            appearance="filled"
            data-cy="ds-switch__thumb-icon"
          />
        </div>
      </div>
    </div>
  </DSFormField>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { DSSwitchEmits } from "../types/emits";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { DS_SWITCH_CLASSES } from "@/src/design_system/form_elements/switch/constants/classes";
import type { DSSwitchModelValue, DSSwitchProps } from "@/src/design_system/form_elements/switch/types/props";
import { DS_SWITCH_LABEL_POSITION } from "@/src/design_system/form_elements/switch/types/switchLabelPosition";
import DSFormField from "@/src/design_system/form_elements/form_field/components/DSFormField.vue";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import { useDSFormField } from "@/src/design_system/form_elements/shared/composables/useDSFormField";
import { useStateClasses } from "@/src/design_system/shared/composables/useStateClasses";
import { useDSFormFieldId } from "@/src/design_system/form_elements/shared/composables/useDSFormFieldId";
import type { DSIconName } from "@/src/design_system/primitives/icon/types/iconName.generated";
import { useBooleanField } from "@/src/design_system/form_elements/shared/composables/useBooleanField";

const props = withDefaults(defineProps<DSSwitchProps>(), {
  hiddenLabel: false,
  disabled: false,
  hasError: false,
  required: false,
  tooltip: undefined,
  errorMessages: undefined,
  labelPosition: DS_SWITCH_LABEL_POSITION.Right,
  rules: undefined,
});

const model = defineModel<DSSwitchModelValue>({ default: false });
const emit = defineEmits<DSSwitchEmits>();
const inputRef = ref<HTMLInputElement>();
const { focus, blur } = useDSInput(inputRef);

defineExpose({ focus, blur });

const { isChecked, checkedSubmitValue, shouldRenderUncheckedInput, onToggle } = useBooleanField({
  model,
  name: computed(() => props.name),
  checkedValue: computed(() => props.checkedValue),
  defaultCheckedValue: "1",
  uncheckedValue: computed(() => props.uncheckedValue),
});

const dsFormField = useDSFormField<DSSwitchModelValue>({
  name: computed(() => props.name),
  errorMessages: computed(() => props.errorMessages),
  value: computed(() => isChecked.value),
  rules: computed(() => props.rules),
});

const { id } = useDSFormFieldId({ props });

const hasErrors = computed(() => props.hasError || !!dsFormField.errorMessages.value.length);

const { stateClasses } = useStateClasses({
  get disabled() {
    return props.disabled;
  },
  get hasError() {
    return props.hasError;
  },
  get errorMessages() {
    return dsFormField.errorMessages.value;
  },
});

const formFieldLabel = computed(() => props.label ?? undefined);

const switchContainerClasses = computed(() => ({
  [DS_SWITCH_CLASSES.Root]: true,
  ...stateClasses.value,
  [DS_SWITCH_CLASSES.Selected]: isChecked.value,
  [DS_SWITCH_CLASSES.LabelLeft]: !!props.label && props.labelPosition === DS_SWITCH_LABEL_POSITION.Left,
  [DS_SWITCH_CLASSES.LabelRight]: !!props.label && props.labelPosition === DS_SWITCH_LABEL_POSITION.Right,
}));

const inputClasses = computed(() => ({
  [DS_SWITCH_CLASSES.Input]: true,
  ...stateClasses.value,
  [DS_SWITCH_CLASSES.InputError]: hasErrors.value,
  [DS_SWITCH_CLASSES.InputSelected]: isChecked.value,
}));

const plateClasses = computed(() => ({
  [DS_SWITCH_CLASSES.Plate]: true,
  ...stateClasses.value,
  [DS_SWITCH_CLASSES.PlateError]: hasErrors.value,
  [DS_SWITCH_CLASSES.PlateSelected]: isChecked.value,
}));

const thumbClasses = computed(() => ({
  [DS_SWITCH_CLASSES.Thumb]: true,
  [DS_SWITCH_CLASSES.ThumbSelected]: isChecked.value,
}));

const thumbIconName = computed<DSIconName>(() => (isChecked.value ? "switch-on" : "switch-off"));
</script>
